/**
 * Le anomalie vicine si raggruppano, e il gruppo dice quante sono e la peggiore.
 *
 * Tre S4 sullo stesso sostegno, viste da lontano, erano un grumo di glifi che
 * non si contava. Il raggruppamento e' per vicinanza sulla tela — una griglia
 * la cui cella segue la finestra — e non per elemento: due anomalie di
 * sostegni diversi ma sovrapposte sullo schermo si coprono lo stesso, e
 * l'operatore vede lo schermo, non la base dati. Avvicinandosi la cella si
 * stringe e i gruppi si sciolgono da soli (GI-247).
 */
import type { MapMark } from '../lineMap'

export interface Placed {
  mark: MapMark
  x: number
  y: number
}

export interface Cluster {
  key: string
  x: number
  y: number
  marks: MapMark[]
  /** Il livello piu' grave del gruppo: 1 e' il peggiore, come nella coda. */
  worst: number
  violates: boolean
}

export type Drawn = { kind: 'one'; mark: MapMark; x: number; y: number } | { kind: 'many'; cluster: Cluster }

function levelOf(mark: MapMark): number {
  return mark.final_level ?? mark.level
}

/**
 * Raggruppa per cella di lato `cell` (in unita' di tela). Un gruppo di uno
 * resta un segno singolo, col suo glifo.
 */
export function clustered(placed: Placed[], cell: number, violates: (mark: MapMark) => boolean): Drawn[] {
  if (!(cell > 0)) return placed.map((one) => ({ kind: 'one', mark: one.mark, x: one.x, y: one.y }))
  const cells = new Map<string, Placed[]>()
  for (const one of placed) {
    const key = `${Math.floor(one.x / cell)}:${Math.floor(one.y / cell)}`
    const found = cells.get(key)
    if (found === undefined) cells.set(key, [one])
    else found.push(one)
  }
  const drawn: Drawn[] = []
  for (const [key, group] of cells) {
    const only = group.length === 1 ? group[0] : undefined
    if (only !== undefined) {
      drawn.push({ kind: 'one', mark: only.mark, x: only.x, y: only.y })
      continue
    }
    drawn.push({
      kind: 'many',
      cluster: {
        key,
        x: group.reduce((sum, one) => sum + one.x, 0) / group.length,
        y: group.reduce((sum, one) => sum + one.y, 0) / group.length,
        marks: group.map((one) => one.mark),
        worst: Math.min(...group.map((one) => levelOf(one.mark))),
        violates: group.some((one) => violates(one.mark)),
      },
    })
  }
  return drawn
}
